import type { MediaItem } from '../components/VintageTV';
import { imageMetadata, type ImageDimensions } from './imageMetadata.generated';

// Engagement photos
import engagement114 from './optimized/CatherineandChrisEngagement-114.webp';
import engagement120 from './optimized/CatherineandChrisEngagement-120.webp';
import engagement121 from './optimized/CatherineandChrisEngagement-121.webp';
import engagement122 from './optimized/CatherineandChrisEngagement-122.webp';

// Guest photos
import guest0 from './guest0.jpg';
import guest1 from './guest1.jpg';
import guest2 from './guest2.jpg';
import guest3 from './guest3.jpg';
import guest4 from './guest4.jpg';
import guest7 from './guest7.jpg';
import guest8 from './guest8.jpg';
import guest9 from './guest9.jpg';
import guest10 from './guest10.jpg';

/**
 * Extract relative path from Vite's import.meta.url-based asset path
 * Handles both dev (/src/assets/...) and production (/assets/...) formats
 */
function getRelativePath(assetUrl: string): string {
    // Try to match /assets/... pattern (production)
    let match = assetUrl.match(/\/assets\/(.+)$/);
    if (match) return match[1];
    
    // Try to match assets/... pattern (without leading slash)
    match = assetUrl.match(/assets\/(.+)$/);
    if (match) return match[1];
    
    // Fallback: return as-is (might already be a relative path)
    return assetUrl;
}

/**
 * Remove Vite's -<hash> before extension (e.g., 0-DaBcDefG.webp -> 0.webp)
 * Vite typically uses 8-character hashes, but we match 6+ to be safe
 */
function stripViteHashIfPresent(filename: string): string {
    return filename.replace(/(-[a-zA-Z0-9]{6,})(\.[a-zA-Z0-9]+)$/, '$2');
}

/**
 * Get dimensions for an imported asset URL
 * Tries multiple lookup strategies to handle dev vs production path differences
 */
function getDimensions(assetUrl: string): ImageDimensions {
    const relativePath = getRelativePath(assetUrl);
    const baseName = relativePath.split('/').pop() || relativePath;
    const noHash = stripViteHashIfPresent(baseName);
    const pathNoHash = stripViteHashIfPresent(relativePath);
    
    // Try multiple lookup strategies in order of likelihood
    const lookupKeys = [
        relativePath,                              // Direct match (dev mode)
        `optimized/${relativePath}`,               // With optimized/ prefix
        pathNoHash,                                // Hash stripped from full path
        `optimized/${pathNoHash}`,                 // Hash stripped + optimized/ prefix
        noHash,                                    // Just filename, hash stripped
        `optimized/engagement-gallery/${noHash}`,  // Full engagement gallery path
    ];
    
    for (const key of lookupKeys) {
        if (imageMetadata[key]) {
            return imageMetadata[key];
        }
    }
    
    return { width: 800, height: 600 };
}

/**
 * Engagement photo images
 */
export const engagementImages = {
    e114: engagement114,
    e120: engagement120,
    e121: engagement121,
    e122: engagement122,
} as const;

/**
 * Guest photo images
 */
export const guestImages = {
    guest0,
    guest1,
    guest2,
    guest3,
    guest4,
    guest7,
    guest8,
    guest9,
    guest10,
} as const;

const galleryModules = import.meta.glob('./optimized/engagement-gallery/*.webp', {
    eager: true,
    import: 'default',
}) as Record<string, string>;

interface GalleryVariants {
    full?: string;
    thumb?: string;
}

interface GalleryEntry extends GalleryVariants {
    dimensions?: ImageDimensions;
}

const galleryVariantMap = new Map<number, GalleryEntry>();

for (const [path, src] of Object.entries(galleryModules)) {
    const match = path.match(/engagement-gallery\/(\d+)(-full)?\.webp$/i);
    if (!match) {
        continue;
    }
    const index = parseInt(match[1], 10);
    const variant = match[2] ? 'full' : 'thumb';

    if (!galleryVariantMap.has(index)) {
        galleryVariantMap.set(index, {});
    }

    const entry = galleryVariantMap.get(index)!;
    entry[variant === 'full' ? 'full' : 'thumb'] = src;
    
    // Store dimensions for the thumbnail variant using the stable glob path
    // (not the hashed src URL which differs between dev and prod)
    if (variant === 'thumb') {
        const metadataKey = path.replace(/^\.\//, '');  // Remove "./" prefix
        entry.dimensions = imageMetadata[metadataKey] ?? { width: 800, height: 600 };
    }
}

const engagementGallerySources = Array.from(galleryVariantMap.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([, variants]) => variants);

const completeGallerySources = engagementGallerySources.filter(
    (variants): variants is Required<GalleryEntry> => Boolean(variants.full && variants.thumb && variants.dimensions),
);

/**
 * Full-resolution engagement photos for ChromeTV and other immersive views
 */
export const engagementMedia: MediaItem[] = completeGallerySources.map(({ full, dimensions }, index) => ({
    id: `engagement-full-${index}`,
    type: 'image',
    src: full,
    alt: 'Engagement Photo',
    width: dimensions.width,
    height: dimensions.height,
}));

/**
 * Optimized engagement photos for masonry grid views with full-res support
 */
export const engagementMediaThumbnails: MediaItem[] = completeGallerySources.map(({ thumb, full, dimensions }, index) => ({
    id: `engagement-${index}`,
    type: 'image',
    src: thumb,
    fullSrc: full,
    alt: 'Engagement Photo',
    width: dimensions.width,
    height: dimensions.height,
}));

/**
 * Guest photos as MediaItem array
 */
export const guestMedia: MediaItem[] = [
    { id: 'guest-0', type: 'image', src: guest0, alt: 'Guest Photo 0', ...getDimensions(guest0) },
    { id: 'guest-1', type: 'image', src: guest1, alt: 'Guest Photo 1', ...getDimensions(guest1) },
    { id: 'guest-2', type: 'image', src: guest2, alt: 'Guest Photo 2', ...getDimensions(guest2) },
    { id: 'guest-3', type: 'image', src: guest3, alt: 'Guest Photo 3', ...getDimensions(guest3) },
    { id: 'guest-4', type: 'image', src: guest4, alt: 'Guest Photo 4', ...getDimensions(guest4) },
    { id: 'guest-7', type: 'image', src: guest7, alt: 'Guest Photo 7', ...getDimensions(guest7) },
    { id: 'guest-8', type: 'image', src: guest8, alt: 'Guest Photo 8', ...getDimensions(guest8) },
    { id: 'guest-9', type: 'image', src: guest9, alt: 'Guest Photo 9', ...getDimensions(guest9) },
    { id: 'guest-10', type: 'image', src: guest10, alt: 'Guest Photo 10', ...getDimensions(guest10) },
];

/**
 * All wedding photos combined as MediaItem array
 */
export const allWeddingMediaFull: MediaItem[] = [
    ...engagementMedia,
    ...guestMedia,
];

export const allWeddingMedia: MediaItem[] = [
    ...engagementMediaThumbnails,
    ...guestMedia,
];

